<script setup lang="ts">
import { computed, type Component } from "vue";
import { useRoute } from "vue-router";
import { ChevronRight, Info } from "@lucide/vue";

const props = withDefaults(
  defineProps<{
    label: string;
    to?: string;
    action?: () => void;
    icon?: string;
    iconComponent?: Component;
    activeNames?: string[];
    active?: boolean;
    isExpanded: boolean;
    linkId?: string;
    /**
     * What the collapsed rail's tooltip says, when the label alone does not survive losing its
     * group heading. "General" under a SETTINGS heading reads fine expanded and says nothing at all
     * as a lone icon, so that row passes "Project settings" here. Defaults to the label.
     */
    tooltipText?: string;
    paddingClass?: string;
    dataCy?: string;
    disabled?: boolean;
    disabledReason?: string;
    hasSubNav?: boolean;
    /** The row's page has a flow guide: a "Show me around" ⓘ at the row's right edge while the row is ACTIVE and
     *  the sidebar expanded. A guide spotlights what is on screen, so it is only offered on the page it describes. */
    guide?: boolean;
    /** Why the guide cannot run yet (e.g. no materials to point at). Renders the ⓘ dimmed, with this as its tooltip. */
    guideDisabledReason?: string;
    /** Opt-in: keep the ⓘ reachable on this row even when the sidebar collapses, as a small badge on
     *  the icon's corner instead of fading out. Only meaningful alongside `guide` + an active row. */
    guideAlwaysVisible?: boolean;
  }>(),
  {
    paddingClass: "px-2.5 py-2",
    tooltipText: undefined,
    activeNames: () => [],
    active: undefined,
    disabled: false,
    hasSubNav: false,
    guide: false,
    guideDisabledReason: "",
    guideAlwaysVisible: false,
  }
);

const emit = defineEmits<{
  openGuide: [];
}>();

const route = useRoute();
const isActive = computed(() => props.active ?? props.activeNames.includes(route.name as string));

const baseClass = computed(() => [
  "focus-ring interactive-active gap-px-12 text-type-default duration-fast hover:bg-app-blue-200/30 ease-standard rounded-8 flex h-fit w-full items-center overflow-hidden whitespace-nowrap text-left transition-colors",
  props.paddingClass,
  isActive.value ? "ring-app-blue-500 bg-app-blue-200/20 hover:bg-app-blue-200/40  ring-1" : "  ",
  props.guide && props.isExpanded ? "pr-9" : "",
]);

// The ⓘ sits on top of the row rather than inside it: a button cannot nest in a RouterLink or another
// button, so the row is wrapped and the trigger is absolutely positioned at its right edge. Collapsed,
// it fades out with the label and leaves the tab order, so the rail stays one control per row.
const guideLocked = computed(() => props.guideDisabledReason !== "");

const openGuide = () => {
  if (!guideLocked.value) emit("openGuide");
};

// Collapsed rows normally fade the ⓘ out with the label and drop it from the tab order — a guide
// spotlights what's on screen, which a collapsed rail with no page under it has nothing to offer. An
// always-visible row (the active 3D viewer, whose guide has no precondition and is meant to be
// reachable at any time) gets a small badge on the icon's corner instead.
const showCollapsedBadge = computed(() => props.guideAlwaysVisible && !props.isExpanded);

const guideButtonClass = computed(() =>
  showCollapsedBadge.value
    ? [
        "focus-ring interactive-active duration-fast ease-standard absolute -top-1 -right-1 flex items-center justify-center rounded-full bg-slate-0 p-px-2 ring-1 ring-slate-250",
        guideLocked.value ? "interactive-disabled" : "",
      ]
    : [
        "focus-ring interactive-active duration-fast ease-standard rounded-4 text-app-blue-800/50 hover:text-app-blue-800 hover:bg-app-blue-200/40 absolute top-1/2 right-2 flex -translate-y-1/2 items-center justify-center p-px-2 transition-all will-change-[opacity]",
        props.isExpanded ? "opacity-100" : "pointer-events-none opacity-0",
        guideLocked.value ? "interactive-disabled" : "",
      ]
);
</script>

<template>
  <div class="relative">
    <!-- Locked / not-yet-available (a step that needs an earlier step done first). Renders a muted,
       non-interactive button and surfaces the reason on hover, expanded or collapsed.
       `aria-disabled` rather than the native attribute, deliberately: Chrome fires no mouse events on a
       natively disabled control, so the tooltip explaining *why* the row is locked would never appear —
       it only used to because the old wrapper element received the hover instead. It also keeps the row
       focusable, so a keyboard user reaches the same explanation. There is no click handler on this
       branch, so nothing needs guarding. Same reasoning as CreateGroupMenu.vue's Speckle item. -->
    <button
      v-if="disabled"
      :id="linkId"
      v-tooltip="{ text: disabledReason || label, variant: 'label', placement: 'right' }"
      type="button"
      aria-disabled="true"
      :aria-label="disabledReason ? `${label} — ${disabledReason}` : label"
      :class="[
        'gap-px-12 text-type-default rounded-8 interactive-disabled flex h-fit w-full items-center overflow-hidden text-left whitespace-nowrap',
        paddingClass,
      ]">
      <component
        :is="iconComponent"
        v-if="iconComponent"
        :stroke-width="1.5"
        color="var(--color-app-blue-800)"
        class="icon-md shrink-0" />
      <font-awesome-icon
        v-else-if="icon"
        :icon="icon"
        class="icon-md shrink-0" />
      <span
        :class="[
          't-small-bold duration-fast ease-standard text-inherit transition-opacity will-change-[opacity]',
          isExpanded ? 'opacity-100' : 'opacity-0',
        ]"
        aria-hidden="true">
        {{ label }}
      </span>
    </button>

    <RouterLink
      v-else-if="to"
      :id="linkId"
      v-tooltip="{ text: tooltipText ?? label, variant: 'label', placement: 'right', disabled: isExpanded }"
      :to="to"
      :aria-label="label"
      :aria-current="isActive ? 'page' : undefined"
      :data-cy="dataCy"
      :class="baseClass">
      <component
        :is="iconComponent"
        v-if="iconComponent"
        :stroke-width="1.5"
        color="var(--color-app-blue-800)"
        class="icon-md shrink-0" />
      <font-awesome-icon
        v-else-if="icon"
        :icon="icon"
        class="icon-md shrink-0" />
      <span
        :class="[
          't-small-bold duration-fast ease-standard text-inherit transition-opacity will-change-[opacity]',
          isExpanded ? 'opacity-100' : 'opacity-0',
        ]"
        aria-hidden="true">
        {{ label }}
      </span>
      <!-- Decorative indicator that this item opens a sub-navigation. -->
      <ChevronRight
        v-if="hasSubNav"
        :stroke-width="1.5"
        aria-hidden="true"
        :class="[
          'icon-sm text-app-blue-800/30 duration-fast ease-standard ml-auto shrink-0 transition-opacity will-change-[opacity]',
          isExpanded ? 'opacity-100' : 'opacity-0',
        ]" />
    </RouterLink>

    <button
      v-else
      :id="linkId"
      v-tooltip="{ text: tooltipText ?? label, variant: 'label', placement: 'right', disabled: isExpanded }"
      type="button"
      :aria-label="label"
      :data-cy="dataCy"
      :class="baseClass"
      @click="action?.()">
      <component
        :is="iconComponent"
        v-if="iconComponent"
        :stroke-width="1.5"
        color="var(--color-app-blue-800)"
        class="icon-md shrink-0" />
      <font-awesome-icon
        v-else-if="icon"
        :icon="icon"
        class="icon-md shrink-0" />
      <span
        :class="[
          't-small-bold duration-fast ease-standard text-inherit transition-opacity will-change-[opacity]',
          isExpanded ? 'opacity-100' : 'opacity-0',
        ]"
        aria-hidden="true">
        {{ label }}
      </span>
    </button>

    <button
      v-if="guide && !disabled && isActive"
      :id="linkId ? `${linkId}-guide` : undefined"
      v-tooltip="{ text: guideLocked ? guideDisabledReason : 'Show me around', variant: 'label', placement: 'right' }"
      type="button"
      :class="guideButtonClass"
      :aria-label="guideLocked ? `Show me around: ${label} — ${guideDisabledReason}` : `Show me around: ${label}`"
      :aria-disabled="guideLocked || undefined"
      :aria-hidden="!isExpanded && !showCollapsedBadge"
      :tabindex="isExpanded || showCollapsedBadge ? undefined : -1"
      @click="openGuide">
      <Info
        :class="showCollapsedBadge ? 'icon-xs' : 'icon-sm'"
        class="shrink-0"
        :stroke-width="1.5"
        aria-hidden="true" />
    </button>
  </div>
</template>
